import React from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { scrollProgressSpring } from '../../lib/fluid-animations';

interface ScrollProgressProps {
  className?: string;
  color?: string;
  height?: number;
  position?: 'top' | 'bottom';
}

/**
 * ScrollProgress - Scroll progress indicator bar
 *
 * Thin horizontal progress bar at top/bottom of viewport
 * Uses useScroll + useSpring for smooth progress
 */
const ScrollProgress: React.FC<ScrollProgressProps> = ({
  className = '',
  color,
  height = 2,
  position = 'top'
}) => {
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();

  // Spring-smooth the progress for fluid feel
  const scaleX = useSpring(scrollYProgress, scrollProgressSpring);

  if (prefersReducedMotion) {
    // Still show progress but without spring animation
    return (
      <motion.div
        className={`fixed ${position === 'top' ? 'top-0' : 'bottom-0'} left-0 right-0 z-[100] origin-left ${className}`}
        style={{
          height,
          backgroundColor: color || 'var(--color-primary)',
          scaleX: scrollYProgress
        }}
        aria-hidden="true"
      />
    );
  }

  return (
    <motion.div
      className={`fixed ${position === 'top' ? 'top-0' : 'bottom-0'} left-0 right-0 z-[100] origin-left ${className}`}
      style={{
        height,
        backgroundColor: color || 'var(--color-primary)',
        scaleX
      }}
      aria-hidden="true"
    />
  );
};

/**
 * ScrollProgressCircular - Circular scroll progress indicator
 * Good for fixed position corners
 */
export const ScrollProgressCircular: React.FC<{
  className?: string;
  size?: number;
  strokeWidth?: number;
}> = ({
  className = '',
  size = 48,
  strokeWidth = 3
}) => {
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, scrollProgressSpring);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      aria-hidden="true"
    >
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        opacity={0.1}
      />
      {/* Progress circle */}
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--color-primary)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        style={{
          pathLength: prefersReducedMotion ? scrollYProgress : smoothProgress,
          strokeDashoffset: 0
        }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </motion.svg>
  );
};

export default ScrollProgress;
